import {
  // eslint-disable-next-line no-restricted-imports
  GCInstantError,
  PlatformWeb,
} from '@play-co/gcinstant';
import { GCInstant } from 'src/lib/gcinstant';
import { updatePrivateProfile } from 'src/lib/profile';
import { withLoading } from 'src/lib/utils';
import { captureGenericError } from 'src/lib/sentry';
import {
  trackProfileConsentClose,
  trackProfileConsentOpen,
  type ConsentSurface,
} from '../launch/onboarding.analytics';
import { trackEntryFirstSubscribe } from '../launch/entryFirstSubscribe.analytics';
import { isSubscribedToOfficialAccount } from '../chatbot/chatbot.api';

/**
 * Reusable LINE profile-permission consent (the `getProfileAsync` popover — NOT
 * the OA Add-Friend popup). Under a simplified-consent (SC) channel the Mini App
 * launches without the `profile` scope, so `playerName` / `playerPhoto` are empty
 * until the user grants it here. No feature/AB knowledge lives here.
 */

/**
 * Outcome of a profile-consent request.
 * - `granted`: the `profile` scope was granted and a display name is available.
 * - `declined`: the user actively closed the popover (USER_INPUT) — the ONLY
 *   outcome that represents a real refusal.
 * - `unavailable`: the popover can't be shown / no usable profile. Terminal.
 * - `error`: an unexpected SDK / network failure, distinct from a decline.
 */
export type LineProfileConsentResult =
  | 'granted'
  | 'declined'
  | 'unavailable'
  | 'error';

/**
 * Result of a profile-consent request: the {@link LineProfileConsentResult}
 * outcome, plus whether the user is an OA friend afterwards — the SC consent
 * popover can add the OA in the same action, so callers branch on this rather
 * than re-checking subscription (the stale entry snapshot can't see it).
 */
export interface ProfileConsentResult {
  outcome: LineProfileConsentResult;
  oaSubscribed: boolean;
}

/** Whether the profile-permission popover can be shown (a real LINE web platform). */
export function isProfileConsentSupported(): boolean {
  return GCInstant instanceof PlatformWeb;
}

/**
 * Whether the user has granted the LINE `profile` scope — equivalent to having a
 * captured display name. Centralized here so callers don't read `playerName`.
 */
export function hasProfileConsent(): boolean {
  return !!GCInstant.playerName;
}

/**
 * Whether the LINE `profile` scope is currently granted, per the LIFF permission
 * API (`liff.permission.query('profile')`). Authoritative for the question "is a
 * client-side read safe?": when granted, `getFriendshipAsync()` /
 * `getProfileAsync()` resolve silently; when NOT granted they would pop the
 * consent popover. Distinct from {@link hasProfileConsent}, which only infers
 * consent from a captured name. Optional-chains the whole call so an older LIFF
 * SDK without the permission API degrades to `false` (no query, no error), and
 * never throws — an unexpected query failure is reported to Sentry and resolves
 * to `false`.
 */
export async function hasGrantedProfilePermission(): Promise<boolean> {
  try {
    const permission = await window.liff?.permission?.query?.('profile');
    return permission?.state === 'granted';
  } catch (error) {
    captureGenericError('LIFF profile permission query failed', error);
    return false;
  }
}

/**
 * Show the profile-permission popover ONCE, emit the bracketing
 * ResubscribePopupOpen/Close analytics (`popupType: 'profile_consent'`, tagged
 * with `origin`), and map the result. Skips the popover AND its analytics when
 * there's nothing to show: returns `granted` immediately if the name is already
 * captured (scope already granted) and `unavailable` off LINE web. Never throws —
 * a consent hiccup must not block launch.
 */
export async function requestProfileConsent(
  origin: ConsentSurface,
): Promise<ProfileConsentResult> {
  if (!isProfileConsentSupported()) {
    return { outcome: 'unavailable', oaSubscribed: false };
  }
  if (hasProfileConsent()) return { outcome: 'granted', oaSubscribed: false };

  const allTimeViewCount = await trackProfileConsentOpen(origin);

  // Mirror requestOfficialAccountFriend: keep Open/Close balanced and never throw
  // even if withLoading fails (an unexpected hiccup maps to `error`), so a consent
  // problem can't abort launch at the splash.
  let outcome: LineProfileConsentResult = 'error';
  let oaSubscribed = false;
  try {
    ({ outcome, oaSubscribed } = await withLoading(async () => {
      const result = await captureProfileConsent();
      // The SC consent popover can add the OA in the same tap, but a grant
      // doesn't imply it — confirm via the subscription check (the client path,
      // since the `profile` scope was just granted) so the close event's
      // `didAddOAFriend` is accurate.
      return {
        outcome: result,
        oaSubscribed:
          result === 'granted' ? await isSubscribedToOfficialAccount() : false,
      };
    }));
  } catch (error) {
    captureGenericError('requestProfileConsent failed', error);
  } finally {
    trackProfileConsentClose(origin, outcome, allTimeViewCount, oaSubscribed);
    if (oaSubscribed) {
      trackEntryFirstSubscribe('profileConsent', origin);
    }
  }

  return { outcome, oaSubscribed };
}

/**
 * Show the popover and map the outcome. On a grant, mirrors the captured
 * name/photo onto the instance (getProfileAsync doesn't always write the photo
 * back) and persists it via {@link updatePrivateProfile}, so a later read — e.g.
 * Application.ts's updatePrivateProfile() — is correct too.
 */
async function captureProfileConsent(): Promise<LineProfileConsentResult> {
  if (!(GCInstant instanceof PlatformWeb)) return 'unavailable';

  try {
    const profile = await GCInstant.getProfileAsync();

    // No name means nothing usable to capture (terminal).
    if (!profile?.playerName) return 'unavailable';

    // Update the instance so the new name/photo are available immediately
    GCInstant.playerName = profile.playerName;
    if (profile.playerPhoto) {
      GCInstant.playerPhoto = profile.playerPhoto;
    }

    // Update the profile on the server
    updatePrivateProfile();

    return 'granted';
  } catch (error) {
    return error instanceof GCInstantError && error.code === 'USER_INPUT'
      ? 'declined'
      : 'error';
  }
}

/**
 * Block the user behind a REQUIRED profile-consent gate — the "hard entry gate"
 * (test arm C). Re-prompts ONLY on an explicit decline until the user grants,
 * holding them on the splash by design. Any other outcome (granted / unavailable
 * / error) returns immediately, so a LINE outage can never freeze launch. Each
 * re-prompt is a distinct popover, so each emits its own Open/Close pair.
 */
export async function blockUntilProfileConsentGranted(
  origin: ConsentSurface,
): Promise<ProfileConsentResult> {
  for (;;) {
    const result = await requestProfileConsent(origin);
    if (result.outcome !== 'declined') return result;
  }
}
